import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '~/lib/utils';
import { ScrollRevealHeadline } from '~/landing_page/components/ScrollRevealHeadline';
import landingType from '~/landing_page/styles/landingTypography.module.css';
import { KeyPointsCard } from './cards/KeyPointsCard';
import { CsvMetricsCard } from './cards/CsvMetricsCard';
import { FigmaArtboardsCard } from './cards/FigmaArtboardsCard';
import { NotionCard } from './cards/NotionCard';
import { SlackCard } from './cards/SlackCard';
import { LinearCard } from './cards/LinearCard';
import { CalendarCard } from './cards/CalendarCard';
import { GmailCard } from './cards/GmailCard';
import { PowerPointVisual } from './PowerPointVisual';
import {
  DEFAULT_SLIDE_PAIRS,
  ProblemSectionSlideBrowser,
} from './desktop/SlideBrowser';
import { NotificationsChaos } from './desktop/NotificationsChaos';
import styles from './ProblemSection.module.css';

const HOLD_AFTER_TYPING_MS = 2400;
const DISSOLVE_MS = 260;
const PAUSE_BEFORE_NEXT_MS = 700;

export type ProblemSectionVariant = 'scroll' | 'desktop';

export interface ProblemSectionProps {
  className?: string;
  headline?: string;
  variant?: ProblemSectionVariant;
}

interface CardConfig {
  key: string;
  node: ReactNode;
  width: number;
  chaos: { ox: number; oy: number; r: number };
  final: { ox: number; oy: number };
}

const GAP = 16;

const CARDS: CardConfig[] = [
  { key: 'key-points',  node: <KeyPointsCard />,      width: 240, chaos: { ox: -70,  oy: -80,  r: -12 }, final: { ox: -540, oy: 0 } },
  { key: 'csv-metrics', node: <CsvMetricsCard />,     width: 240, chaos: { ox:  55,  oy: -45,  r:   9 }, final: { ox: -540, oy: 0 } },
  { key: 'figma',       node: <FigmaArtboardsCard />, width: 240, chaos: { ox: -100, oy:  35,  r:  -7 }, final: { ox: -540, oy: 0 } },
  { key: 'notion',      node: <NotionCard />,         width: 240, chaos: { ox:  80,  oy:  65,  r:  11 }, final: { ox: -540, oy: 0 } },
  { key: 'slack',       node: <SlackCard />,          width: 240, chaos: { ox:  15,  oy: -100, r:  -5 }, final: { ox:  300, oy: 0 } },
  { key: 'linear',      node: <LinearCard />,         width: 240, chaos: { ox: -45,  oy:  80,  r:  14 }, final: { ox:  300, oy: 0 } },
  { key: 'calendar',    node: <CalendarCard />,       width: 240, chaos: { ox:  95,  oy: -15,  r: -10 }, final: { ox:  300, oy: 0 } },
  { key: 'gmail',       node: <GmailCard />,          width: 240, chaos: { ox: -25,  oy:  45,  r:   6 }, final: { ox:  300, oy: 0 } },
];

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const smoothstep = (t: number) => t * t * (3 - 2 * t);

type DesktopCyclePhase = 'typing' | 'holding' | 'dissolving' | 'pausing';

export const ProblemSection = ({
  className,
  headline = 'Explaining and presenting\nshouldn\'t be this hard.',
  variant = 'scroll',
}: ProblemSectionProps) => {
  const sectionRef = useRef<HTMLElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const pptRef = useRef<HTMLDivElement>(null);
  const rafRef = useRef<number>(0);

  const [pairIndex, setPairIndex] = useState(0);
  const [cyclePhase, setCyclePhase] = useState<DesktopCyclePhase>('typing');
  const [desktopActive, setDesktopActive] = useState(false);

  const handleTypingComplete = useCallback(() => {
    setCyclePhase((current) => (current === 'typing' ? 'holding' : current));
  }, []);

  useEffect(() => {
    if (variant !== 'desktop') return;
    const el = sectionRef.current;
    if (!el) return;

    if (typeof IntersectionObserver === 'undefined') {
      setDesktopActive(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setDesktopActive(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [variant]);

  useEffect(() => {
    if (variant !== 'desktop' || !desktopActive) return;

    if (cyclePhase === 'holding') {
      const t = setTimeout(() => setCyclePhase('dissolving'), HOLD_AFTER_TYPING_MS);
      return () => clearTimeout(t);
    }
    if (cyclePhase === 'dissolving') {
      const t = setTimeout(() => setCyclePhase('pausing'), DISSOLVE_MS);
      return () => clearTimeout(t);
    }
    if (cyclePhase === 'pausing') {
      const t = setTimeout(() => {
        setPairIndex((p) => (p + 1) % DEFAULT_SLIDE_PAIRS.length);
        setCyclePhase('typing');
      }, PAUSE_BEFORE_NEXT_MS);
      return () => clearTimeout(t);
    }
  }, [cyclePhase, variant, desktopActive]);

  const getProgress = useCallback(() => {
    const section = sectionRef.current;
    if (!section) return 0;
    const rect = section.getBoundingClientRect();
    const scrollable = section.offsetHeight - window.innerHeight;
    if (scrollable <= 0) return 0;
    return clamp(-rect.top / scrollable, 0, 1);
  }, []);

  const computeFinalYPositions = useCallback(() => {
    const cy = window.innerHeight / 2;
    const left = [0, 1, 2, 3];
    const right = [4, 5, 6, 7];

    const stack = (indices: number[]) => {
      const heights = indices.map((i) => cardRefs.current[i]?.offsetHeight ?? 80);
      const total = heights.reduce((s, h) => s + h, 0) + GAP * (heights.length - 1);
      let y = cy - total / 2;
      const positions: number[] = [];
      heights.forEach((h) => {
        positions.push(y);
        y += h + GAP;
      });
      return positions;
    };

    const leftY = stack(left);
    const rightY = stack(right);
    return [...leftY, ...rightY];
  }, []);

  const render = useCallback((p: number) => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const cx = vw / 2;
    const cy = vh / 2;

    const finalYPositions = computeFinalYPositions();

    CARDS.forEach((card, i) => {
      const el = cardRefs.current[i];
      if (!el) return;

      const chaosX = cx - card.width / 2 + card.chaos.ox;
      const chaosY = cy - 40 + card.chaos.oy;
      const finalX = cx + card.final.ox;
      const finalY = finalYPositions[i];

      let x: number, y: number, s: number, r: number, a: number;

      if (p <= 0.40) {
        const bandWidth = 0.40 / CARDS.length;
        const start = i * bandWidth;
        const end = start + bandWidth;

        if (p < start) {
          x = chaosX; y = chaosY; s = 0; r = card.chaos.r; a = 0;
        } else {
          const t = smoothstep(clamp((p - start) / (end - start), 0, 1));
          x = chaosX;
          y = chaosY;
          s = lerp(0, 1, t);
          r = card.chaos.r;
          a = clamp(t * 3, 0, 1);
        }
      } else {
        const raw = clamp((p - 0.40) / 0.60, 0, 1);
        const stagger = i * 0.02;
        const st = smoothstep(clamp((raw - stagger) / (1 - stagger), 0, 1));
        x = lerp(chaosX, finalX, st);
        y = lerp(chaosY, finalY, st);
        s = 1;
        r = lerp(card.chaos.r, 0, st);
        a = 1;
      }

      el.style.transform = `translate(${x}px, ${y}px) scale(${s}) rotate(${r}deg)`;
      el.style.opacity = String(a);
    });

    const spread = clamp((p - 0.40) / 0.60, 0, 1);

    if (pptRef.current) {
      pptRef.current.className = cn(
        styles.powerpoint,
        spread > 0.4 && styles.powerpointRevealed,
      );
    }

  }, []);

  useEffect(() => {
    if (variant !== 'scroll') return;

    let lastP = -1;

    const tick = () => {
      const p = getProgress();
      if (p !== lastP) {
        render(p);
        lastP = p;
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    render(getProgress());
    rafRef.current = requestAnimationFrame(tick);

    return () => cancelAnimationFrame(rafRef.current);
  }, [getProgress, render, variant]);

  if (variant === 'desktop') {
    return (
      <section
        ref={sectionRef}
        className={cn(styles.desktopSection, className)}
        data-name="ProblemSection"
        data-variant="desktop"
      >
        <ScrollRevealHeadline
          className={cn(landingType.sectionHeadline, styles.headline, styles.desktopHeadline)}
        >
          <span className={styles.desktopHeadlineLine}>Explaining and presenting</span>
          <span className={styles.desktopHeadlineLine}>
            shouldn&rsquo;t be this <span className={styles.desktopHeadlineEmphasis}>hard</span>
            <span className={styles.desktopHeadlineEmphasis}>.</span>
          </span>
        </ScrollRevealHeadline>
        <div className={styles.desktopFrameStack}>
          <div className={styles.desktopFrameUnit}>
          <div className={styles.desktopFrame}>
            {desktopActive ? (
              <>
                <ProblemSectionSlideBrowser
                  pairIndex={pairIndex}
                  dissolving={cyclePhase === 'dissolving' || cyclePhase === 'pausing'}
                  onTypingComplete={handleTypingComplete}
                />
                <NotificationsChaos
                  cycleKey={pairIndex}
                  dissolving={cyclePhase === 'dissolving' || cyclePhase === 'pausing'}
                />
              </>
            ) : null}
            <img
              src="/landing/problem-section/menu-bar-light.svg"
              alt=""
              className={styles.desktopMenuBar}
            />
            <img
              src="/landing/problem-section/dock-light.svg"
              alt=""
              className={styles.desktopDock}
            />
          </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      ref={sectionRef}
      className={cn(styles.section, className)}
      data-name="ProblemSection"
    >
      <div className={styles.stickyViewport}>
        <div className={styles.scene}>
          {CARDS.map((card, i) => (
            <div
              key={card.key}
              ref={(el) => { cardRefs.current[i] = el; }}
              className={styles.widgetCard}
              style={{ width: card.width, opacity: 0 }}
            >
              {card.node}
            </div>
          ))}

          <div ref={pptRef} className={styles.powerpoint}>
            <PowerPointVisual />
          </div>

          <div className={styles.heading}>
            <ScrollRevealHeadline className={cn(landingType.sectionHeadline, styles.headline)}>
              {headline}
            </ScrollRevealHeadline>
          </div>
        </div>
      </div>
    </section>
  );
};

ProblemSection.displayName = 'ProblemSection';
